import React, { useRef, useEffect } from 'react';
import { Sparkles, X, Loader2 } from 'lucide-react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ContentStrategy } from '../../../services/tenant-settings-api';
import { MessageBubble, ChatMessage, ChatInputArea, ChatInputAreaHandle } from '@contentgrowth/llm-service/ui/react';
import { apiClient } from '@/app/api/client';
import { useChat } from '@/hooks/useChat';

interface ContentStrategyAIChatProps {
    onClose: () => void;
    onUpdateStrategy: (strategy: Partial<ContentStrategy>) => void;
    currentStrategy: ContentStrategy;
    initialContext?: string;
}

export const ContentStrategyAIChat: React.FC<ContentStrategyAIChatProps> = ({
    onClose,
    onUpdateStrategy,
    currentStrategy,
    initialContext
}) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const hasInitializedRef = useRef(false);
    const chatInputRef = useRef<ChatInputAreaHandle>(null);

    // Custom handler for useChat to use the specific endpoint
    const chatHandler = async (messages: ChatMessage[], signal?: AbortSignal) => {
        const response = await apiClient.post('/api/tenant/content-strategy-chat', {
            messages: messages.map(m => ({
                role: m.role,
                content: m.content
            })),
            currentStrategy
        }, {
            signal
        });
        return response.data;
    };

    const {
        messages,
        input,
        handleInputChange,
        handleSubmit,
        sendMessage,
        isLoading,
        error,
        stop
    } = useChat({
        handler: chatHandler,
        initialMessages: [
            {
                id: 'welcome',
                role: 'assistant',
                content: "Hi! I'm your Content Strategy Co-pilot. I can help you define your target audience, brand voice, and content pillars. Where would you like to start?",
            }
        ],
        onResponse: (data) => {
            if (data.strategyUpdated) {
                onUpdateStrategy({});
            }
            return data.message;
        },
        inputRef: chatInputRef
    });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Auto-send initial message if context is provided
    useEffect(() => {
        if (initialContext && !hasInitializedRef.current) {
            hasInitializedRef.current = true;
            sendMessage(`I want to work on the ${initialContext} section.`);
        }
    }, [initialContext, sendMessage]);

    return (
        <div className="flex flex-col h-full bg-white border-l border-gray-200 shadow-xl w-96 fixed right-0 top-0 bottom-0 z-50">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center">
                    <Sparkles className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="font-medium text-gray-900">AI Co-pilot</h3>
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg, index) => (
                    <MessageBubble key={msg.id || index} message={msg} isUser={msg.role === 'user'} />
                ))}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                            <div className="flex items-center space-x-2">
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                <span className="text-sm text-gray-500">Thinking...</span>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
                {error && (
                    <div className="mb-2 flex items-center text-sm text-red-600 bg-red-50 p-2 rounded-md">
                        <ExclamationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                        <span>{error.message}</span>
                    </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
                    <ChatInputArea
                        ref={chatInputRef}
                        value={input}
                        onChange={handleInputChange}
                        onSubmit={(msg) => handleSubmit({ preventDefault: () => { } } as React.FormEvent)}
                        isSending={isLoading}
                        onStop={stop}
                        showInputForm={true}
                        currentTask={null}
                        placeholder="Type a message..."
                    />
                </form>
            </div>
        </div>
    );
};
